import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getSupportPractitionerCommunicationsBootstrap,
  sendSupportPractitionerGenericCommunication,
  sendSupportPractitionerTherapyReportReminder,
} from 'src/supportPortal/services/customCommunicationService'

const DEFAULT_MODES = ['whatsapp']

function getPractitionerName(practitioner) {
  return (
    practitioner?.name ||
    practitioner?.fullName ||
    practitioner?.displayName ||
    [practitioner?.firstName, practitioner?.lastName].filter(Boolean).join(' ') ||
    'Practitioner'
  )
}

function buildTargetPayload(practitioner) {
  return {
    practitionerId: practitioner?.id,
    practitionerSlug: practitioner?.slug,
  }
}

function DispatchResult({ result }) {
  if (!result) return null

  const dispatches = Array.isArray(result?.dispatches) ? result.dispatches : []
  const reminderCounts = result?.counts

  return (
    <Alert severity={result?.sent === false ? 'info' : 'success'} variant="outlined" sx={{ borderRadius: 2 }}>
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {result?.reason || 'Communication request completed.'}
        </Typography>
        {dispatches.length > 0 && (
          <Stack spacing={0.5}>
            {dispatches.map((item) => (
              <Typography key={`${item?.mode}-${item?.status}`} variant="caption" color="text.secondary">
                {item?.mode}: {item?.message || item?.status}
              </Typography>
            ))}
          </Stack>
        )}
        {reminderCounts?.overall?.label && (
          <Typography variant="caption" color="text.secondary">
            Overall pending: {reminderCounts.overall.label}
          </Typography>
        )}
      </Stack>
    </Alert>
  )
}

export default function PractitionerCommunicationDialog({ open, practitioner, onClose }) {
  const [tab, setTab] = useState('reminder')
  const [bootstrap, setBootstrap] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [sending, setSending] = useState(false)
  const [selectedModes, setSelectedModes] = useState(DEFAULT_MODES)
  const [subject, setSubject] = useState('Message from AUMHUM')
  const [message, setMessage] = useState('')
  const [result, setResult] = useState(null)

  const practitionerName = getPractitionerName(practitioner)
  const modeOptions = useMemo(() => {
    const options = Array.isArray(bootstrap?.communicationModes) ? bootstrap.communicationModes : []
    return options.length
      ? options
      : [
          { id: 'call', label: 'Call', configured: true },
          { id: 'whatsapp', label: 'WhatsApp', configured: true },
          { id: 'email', label: 'Email', configured: true },
        ]
  }, [bootstrap])

  useEffect(() => {
    if (!open || !practitioner?.id) return

    let active = true
    async function load() {
      setLoading(true)
      setLoadError('')
      setResult(null)
      const response = await getSupportPractitionerCommunicationsBootstrap(buildTargetPayload(practitioner))
      if (!active) return

      if (response?.ok) {
        setBootstrap(response.data || null)
      } else {
        setLoadError(response?.message || 'Unable to load communication options.')
      }
      setLoading(false)
    }

    load()
    return () => {
      active = false
    }
  }, [open, practitioner])

  const handleModeToggle = (modeId) => {
    setSelectedModes((previous) => {
      if (previous.includes(modeId)) {
        return previous.filter((item) => item !== modeId)
      }
      return [...previous, modeId]
    })
  }

  const handleSendReminder = async () => {
    if (!practitioner?.id) return
    setSending(true)
    setResult(null)
    const response = await sendSupportPractitionerTherapyReportReminder(buildTargetPayload(practitioner))
    setSending(false)

    if (response?.ok) {
      setResult(response.data || null)
      if (response?.data?.sent === false) {
        toast.info(response?.data?.reason || 'No reminder was sent.')
      } else {
        toast.success('Therapy report reminder sent.')
      }
    } else {
      toast.error(response?.message || 'Unable to send therapy report reminder.')
    }
  }

  const handleSendGeneric = async () => {
    const trimmedMessage = message.trim()
    if (!selectedModes.length) {
      toast.error('Select at least one communication mode.')
      return
    }
    if (!trimmedMessage) {
      toast.error('Enter a message before sending.')
      return
    }

    setSending(true)
    setResult(null)
    const response = await sendSupportPractitionerGenericCommunication({
      ...buildTargetPayload(practitioner),
      modes: selectedModes,
      subject: subject.trim() || 'Message from AUMHUM',
      message: trimmedMessage,
    })
    setSending(false)

    if (response?.ok) {
      setResult(response.data || null)
      const dispatches = Array.isArray(response?.data?.dispatches) ? response.data.dispatches : []
      if (dispatches.length > 0 && dispatches.every((item) => item?.status === 'failed')) {
        toast.error('No communication channel could be sent.')
      } else {
        toast.success('Practitioner communication requested.')
      }
    } else {
      toast.error(response?.message || 'Unable to send practitioner communication.')
    }
  }

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CampaignOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Communicate
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {practitionerName}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {loading ? (
            <Stack spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading communication options...
              </Typography>
            </Stack>
          ) : loadError ? (
            <Alert severity="error">{loadError}</Alert>
          ) : (
            <>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
                <Tab value="reminder" label="Report Reminder" />
                <Tab value="generic" label="Generic" />
              </Tabs>

              {tab === 'reminder' ? (
                <Stack spacing={2}>
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    Sends the therapy report completion reminder only if this therapist has pending report
                    work or events not marked complete. The message includes yesterday, this week, this
                    month, and overall counts, plus the payout-delay warning.
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={handleSendReminder}
                    disabled={sending}
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  >
                    {sending ? 'Sending...' : 'Send Report Reminder'}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <FormGroup row>
                    {modeOptions.map((option) => (
                      <FormControlLabel
                        key={option?.id}
                        control={
                          <Checkbox
                            checked={selectedModes.includes(option?.id)}
                            disabled={sending || option?.configured === false}
                            onChange={() => handleModeToggle(option?.id)}
                          />
                        }
                        label={option?.label || option?.id}
                      />
                    ))}
                  </FormGroup>
                  <Alert severity="info" icon={<WhatsAppIcon />} variant="outlined" sx={{ borderRadius: 2 }}>
                    If WhatsApp is outside the 24-hour window, the backend sends an important-message
                    template and queues the actual text for 24 hours.
                  </Alert>
                  <TextField
                    label="Email Subject"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    disabled={sending}
                    fullWidth
                  />
                  <TextField
                    label="Message"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    disabled={sending}
                    minRows={5}
                    multiline
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendGeneric}
                    disabled={sending}
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  >
                    {sending ? 'Sending...' : 'Send Communication'}
                  </Button>
                </Stack>
              )}

              <DispatchResult result={result} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
